import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

import {
	DuelFeatureSamePng,
	DuelGiftBoxPng,
	DuelHeroSwordsJpg,
	DuelHistoryJpg,
	DuelIconChevronSvg,
	DuelIconSwordsSvg,
	DuelRatingShieldPng,
	DuelShieldJpg,
	DuelTrophyPng,
} from '../../../assets/duels';
import type { DuelHubResponse, DuelSnapshotDto } from '../../../services/api';
import { GoldGradientText } from '../common/GoldGradientText';
import { TouchableOpacity } from '../common/Touchable';
import { formatCountdown, useCountdown } from '../Tournaments/useCountdown';
import { duelText } from './duelsText';
import { daysUntil, divisionBadge, formatScore } from './duelsTheme';
import {
	DuelButton,
	DuelClockText,
	DuelFinishedFigures,
	DuelPanel,
	DuelPill,
	DuelProgressBar,
	duelTextStyles,
} from './DuelsUi';

const HUB_FIGURE_SLOTS = 7;

const Chevron = ({ scale }: { scale: number }) => (
	<DuelIconChevronSvg width={16 * scale} height={16 * scale} />
);

type CardProps = { width: number; scale: number };

type HeroProps = CardProps & {
	onFind: () => void;
	finding: boolean;
	disabled: boolean;
};

export const DuelHero = ({ width, scale, onFind, finding, disabled }: HeroProps) => {
	const { t } = useTranslation();
	const inner = width - 24 * scale;
	const features = [
		{
			image: DuelFeatureSamePng,
			title: 'T_SITE_DUELS_FEATURE_SAME_TITLE',
			body: 'T_SITE_DUELS_FEATURE_SAME_BODY',
		},
		{
			image: DuelRatingShieldPng,
			title: 'T_SITE_DUELS_FEATURE_FAIR_TITLE',
			body: 'T_SITE_DUELS_FEATURE_FAIR_BODY',
		},
		{
			image: DuelTrophyPng,
			title: 'T_SITE_DUELS_FEATURE_CLIMB_TITLE',
			body: 'T_SITE_DUELS_FEATURE_CLIMB_BODY',
		},
	];

	return (
		<DuelPanel scale={scale} width={width} highlighted>
			<View style={[styles.row, { columnGap: 12 * scale }]}>
				<Image
					source={DuelHeroSwordsJpg}
					style={{ width: 170 * scale, height: 134 * scale, borderRadius: 8 * scale }}
					resizeMode='cover'
				/>
				<View style={[styles.flex, { rowGap: 4 * scale }]}>
					<Text style={[duelTextStyles.title, { fontSize: 22 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_HERO_TITLE')}
					</Text>
					<GoldGradientText
						text={duelText(t, 'T_SITE_DUELS_HERO_TITLE_ACCENT')}
						width={inner - 182 * scale}
						height={28 * scale}
						fontSize={24 * scale}
						fontWeight='900'
						align='left'
					/>
					<Text style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_HERO_SUBTITLE')}
					</Text>
				</View>
			</View>
			<View style={[styles.row, { columnGap: 8 * scale }]}>
				{features.map(({ image, title, body }) => (
					<View key={title} style={[styles.flex, styles.center, { rowGap: 2 * scale }]}>
						<Image
							source={image}
							style={{ width: 30 * scale, height: 30 * scale }}
							resizeMode='contain'
						/>
						<Text style={[duelTextStyles.title, styles.centerText, { fontSize: 11 * scale }]}>
							{duelText(t, title)}
						</Text>
						<Text style={[duelTextStyles.muted, styles.centerText, { fontSize: 10 * scale }]}>
							{duelText(t, body)}
						</Text>
					</View>
				))}
			</View>
			<DuelButton
				label={duelText(t, finding ? 'T_SITE_DUELS_FINDING' : 'T_SITE_DUELS_FIND')}
				onPress={onFind}
				width={inner}
				scale={scale}
				busy={finding}
				disabled={disabled}
				icon={<DuelIconSwordsSvg width={18 * scale} height={18 * scale} />}
			/>
		</DuelPanel>
	);
};

type DivisionProps = CardProps & { rating: DuelHubResponse['rating']; onPress?: () => void };

export const DuelDivisionCard = ({ width, scale, rating, onPress }: DivisionProps) => {
	const { t } = useTranslation();
	const next = rating.nextDivision;
	const progress = next ? Math.min(1, rating.rating / Math.max(1, next.minRating)) : 1;

	return (
		<TouchableOpacity onPress={onPress} disabled={!onPress} accessibilityRole='button'>
			<DuelPanel scale={scale} width={width}>
				<View style={[styles.row, { columnGap: 12 * scale }]}>
					<Image
						source={divisionBadge(rating.division.name)}
						style={{ width: 56 * scale, height: 56 * scale }}
						resizeMode='contain'
					/>
					<View style={[styles.flex, { rowGap: 4 * scale }]}>
						<Text style={[duelTextStyles.title, { fontSize: 17 * scale }]}>
							{rating.division.label}
						</Text>
						<Text style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_RATING', [formatScore(rating.rating)])}
						</Text>
						<DuelProgressBar value={progress} width={width - 112 * scale} scale={scale} />
						<Text style={[duelTextStyles.body, styles.end, { fontSize: 11 * scale }]}>
							{next
								? duelText(t, 'T_SITE_DUELS_NEXT_DIVISION', [
										next.name,
										formatScore(next.minRating),
									])
								: duelText(t, 'T_SITE_DUELS_TOP_DIVISION')}
						</Text>
					</View>
					{onPress ? <Chevron scale={scale} /> : null}
				</View>
			</DuelPanel>
		</TouchableOpacity>
	);
};

type SeasonProps = CardProps & { season: DuelHubResponse['season']; onPress?: () => void };

export const DuelSeasonCard = ({ width, scale, season, onPress }: SeasonProps) => {
	const { t } = useTranslation();
	if (!season.season) {
		return (
			<DuelPanel scale={scale} width={width}>
				<Text style={[duelTextStyles.body, { fontSize: 14 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_NO_SEASON')}
				</Text>
			</DuelPanel>
		);
	}

	const days = daysUntil(season.season.endsAt);
	const target = season.nextMilestone?.threshold ?? null;
	const progress = target ? Math.min(1, season.points / target) : 1;

	return (
		<TouchableOpacity onPress={onPress} disabled={!onPress} accessibilityRole='button'>
			<DuelPanel scale={scale} width={width}>
				<View style={[styles.row, { columnGap: 12 * scale }]}>
					<View style={[styles.flex, { rowGap: 4 * scale }]}>
						<View style={[styles.row, styles.between]}>
							<DuelClockText text={season.season.name} scale={scale} fontSize={17} white />
							<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
								{days
									? duelText(t, 'T_SITE_DUELS_DAYS_LEFT', [days])
									: duelText(t, 'T_SITE_DUELS_ENDS_TODAY')}
							</Text>
						</View>
						<Text style={[duelTextStyles.title, { fontSize: 26 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_POINTS', [formatScore(season.points)])}
						</Text>
						<DuelProgressBar value={progress} width={width - 124 * scale} scale={scale} />
						<Text style={[duelTextStyles.body, { fontSize: 11 * scale }]}>
							{target
								? duelText(t, 'T_SITE_DUELS_NEXT_MILESTONE', [formatScore(target)])
								: duelText(t, 'T_SITE_DUELS_ALL_MILESTONES')}
						</Text>
					</View>
					<Image
						source={DuelGiftBoxPng}
						style={{ width: 56 * scale, height: 56 * scale }}
						resizeMode='contain'
					/>
					{onPress ? <Chevron scale={scale} /> : null}
				</View>
			</DuelPanel>
		</TouchableOpacity>
	);
};

type ShortcutProps = CardProps & { onPress: () => void };

export const DuelHistoryShortcut = ({ width, scale, onPress }: ShortcutProps) => {
	const { t } = useTranslation();

	return (
		<TouchableOpacity onPress={onPress} accessibilityRole='button'>
			<DuelPanel scale={scale} width={width}>
				<View style={[styles.row, { columnGap: 12 * scale }]}>
					<Image
						source={DuelHistoryJpg}
						style={{ width: 52 * scale, height: 46 * scale, borderRadius: 6 * scale }}
						resizeMode='contain'
					/>
					<View style={[styles.flex, { rowGap: 2 * scale }]}>
						<Text style={[duelTextStyles.gold, { fontSize: 16 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_HISTORY_CARD_TITLE')}
						</Text>
						<Text style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_HISTORY_CARD_BODY')}
						</Text>
					</View>
					<Chevron scale={scale} />
				</View>
			</DuelPanel>
		</TouchableOpacity>
	);
};

type InProgressProps = CardProps & {
	duel: DuelSnapshotDto;
	onViewResult: () => void;
	onResume: () => void;
};

export const DuelInProgressCard = ({
	width,
	scale,
	duel,
	onViewResult,
	onResume,
}: InProgressProps) => {
	const { t } = useTranslation();
	const remaining = useCountdown(duel.expiresAt ? new Date(duel.expiresAt).getTime() : null);
	const participants = duel.participantCount ?? duel.currentPlayers;
	const finished = duel.finishedCount ?? 0;
	const stillPlaying = Math.max(0, participants - finished);
	const inner = width - 24 * scale;

	return (
		<DuelPanel scale={scale} width={width} highlighted>
			<View
				style={[
					styles.row,
					styles.innerHero,
					{ columnGap: 12 * scale, padding: 8 * scale, borderRadius: 10 * scale },
				]}
			>
				<Image
					source={DuelHeroSwordsJpg}
					style={{ width: 140 * scale, height: 112 * scale, borderRadius: 8 * scale }}
					resizeMode='cover'
				/>
				<View style={[styles.flex, { rowGap: 6 * scale }]}>
					<Text style={[duelTextStyles.title, { fontSize: 22 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_IN_PROGRESS')}
					</Text>
					{duel.expiresAt ? (
						<DuelClockText
							text={duelText(t, 'T_SITE_DUELS_REMAINING', [formatCountdown(remaining)])}
							scale={scale}
						/>
					) : null}
					<Text style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_PLAYERS_FINISHED', [finished, participants])}
					</Text>
					<DuelFinishedFigures
						finished={finished}
						participants={participants}
						slots={HUB_FIGURE_SLOTS}
						scale={scale}
					/>
				</View>
			</View>

			{duel.currentPlayerFinished ? (
				<View style={[styles.row, { columnGap: 8 * scale }]}>
					<DuelPanel scale={scale} width={(inner - 8 * scale) / 2}>
						<Text style={[duelTextStyles.body, { fontSize: 11 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_YOUR_RESULT')}
						</Text>
						<View style={[styles.row, { columnGap: 8 * scale }]}>
							<Image
								source={DuelShieldJpg}
								style={{ width: 40 * scale, height: 40 * scale }}
								resizeMode='contain'
							/>
							<View>
								<Text style={[duelTextStyles.gold, { fontSize: 24 * scale }]}>
									{duel.currentPlayerRank ? `#${duel.currentPlayerRank}` : '—'}
								</Text>
								<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
									{duelText(t, 'T_SITE_DUELS_SCORE_VALUE', [
										formatScore(duel.currentPlayerDisplayScore),
									])}
								</Text>
							</View>
						</View>
						<DuelPill label={duelText(t, 'T_SITE_DUELS_PROVISIONAL')} scale={scale} />
					</DuelPanel>
					<DuelPanel scale={scale} width={(inner - 8 * scale) / 2}>
						<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
							{`${duelText(t, 'T_SITE_DUELS_LOCKED_IN')}\n\n${[
								duelText(t, 'T_SITE_DUELS_STILL_PLAYING', [stillPlaying]),
								duelText(t, 'T_SITE_DUELS_FINAL_WHEN_CLOSES'),
							].join(' ')}`}
						</Text>
					</DuelPanel>
				</View>
			) : (
				<Text style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_RUN_IN_PROGRESS')}
				</Text>
			)}

			<DuelButton
				label={duelText(
					t,
					duel.currentPlayerFinished ? 'T_SITE_DUELS_VIEW_PROVISIONAL' : 'T_SITE_DUELS_RESUME',
				)}
				onPress={duel.currentPlayerFinished ? onViewResult : onResume}
				width={inner}
				scale={scale}
			/>
		</DuelPanel>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	between: {
		justifyContent: 'space-between',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	end: {
		alignSelf: 'flex-end',
	},
	center: {
		alignItems: 'center',
	},
	centerText: {
		textAlign: 'center',
	},
	innerHero: {
		backgroundColor: 'rgba(20, 4, 2, 0.55)',
	},
});
